/**
 * Interior Moderna — Custom Wishlist System
 * Handles: heart buttons, email capture, wishlist page, localStorage state.
 * Talks to: /api/storefront/wishlist/* on the ops dashboard (Railway).
 */
(function () {
  'use strict';

  // ── Config ──────────────────────────────────────────────────────────────
  var API_BASE = 'https://im-ops-dashboard-production.up.railway.app';
  var STORAGE_KEY = 'im_wishlist_email';
  var STORAGE_IDS_KEY = 'im_wishlist_ids';
  var STORAGE_IDS_TS = 'im_wishlist_ids_ts';
  var IDS_CACHE_MS = 5 * 60 * 1000; // 5 minute cache for variant IDs

  // ── State ───────────────────────────────────────────────────────────────
  var _email = null;
  var _variantIds = new Set();
  var _productIds = new Set();
  var _initialized = false;
  var _wlSort = 'date-desc';
  var _wlFilters = { onSale: false, inStock: false, priceDrop: false };
  var _wlRawItems = null;
  var _wlEnrichMap = null;
  var _wlPageContainer = null;

  // ── Helpers ─────────────────────────────────────────────────────────────

  function escHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getEmail() {
    if (_email) return _email;
    // Check Shopify customer object FIRST (source of truth, injected by wishlist-loader.liquid)
    if (window.__imWishlistEmail) {
      _email = window.__imWishlistEmail.trim().toLowerCase();
      localStorage.setItem(STORAGE_KEY, _email);
      return _email;
    }
    // Fall back to localStorage only if no Shopify customer
    var stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      _email = stored.trim().toLowerCase();
      return _email;
    }
    return null;
  }

  function setEmail(email) {
    email = (email || '').trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return false;
    _email = email;
    localStorage.setItem(STORAGE_KEY, email);
    try {
      if (window.imEvents && typeof window.imEvents.identify === 'function') {
        window.imEvents.identify(email, { source: 'wishlist_email_capture' });
      }
    } catch (_) {}
    return true;
  }

  function apiGet(path) {
    return fetch(API_BASE + path, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }).then(function (r) { return r.json(); });
  }

  function apiPost(path, data) {
    return fetch(API_BASE + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(function (r) { return r.json(); });
  }

  function normalizeCurrency(value) {
    if (value === null || typeof value === 'undefined' || value === '') return null;
    var n = parseFloat(String(value).replace(/[$,\s]/g, ''));
    return Number.isFinite(n) ? n : null;
  }

  function wishlistEventProps(productData, extra) {
    productData = productData || {};
    var props = {
      product_id: productData.productId || productData.shopify_product_id || productData.pid || null,
      variant_id: productData.variantId || productData.shopify_variant_id || productData.vid || null,
      product_handle: productData.handle || productData.product_handle || null,
      sku: productData.sku || null,
      context: productData.context || null,
      source_page: productData.sourcePage || window.location.pathname || null,
      value: normalizeCurrency(productData.price || productData.price_at_add),
      compare_at_price: normalizeCurrency(productData.compareAtPrice || productData.compare_at_price)
    };
    if (extra && typeof extra === 'object') {
      for (var k in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, k)) props[k] = extra[k];
      }
    }
    return props;
  }

  function trackWishlistEvent(eventName, productData, extra) {
    var props = wishlistEventProps(productData, extra);
    try {
      if (window.imEvents && typeof window.imEvents.track === 'function') {
        window.imEvents.track(eventName, {
          sku: props.sku || null,
          properties: props
        });
      }
    } catch (_) {}
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, props));
    } catch (_) {}
    try {
      if (typeof window.gtag === 'function') window.gtag('event', eventName, props);
    } catch (_) {}
  }

  // ── Core Functions ─────────────────────────────────────────────────────

  function loadWishlistIds(forceRefresh) {
    var email = getEmail();
    if (!email) {
      _variantIds = new Set();
      _productIds = new Set();
      updateAllHearts();
      return Promise.resolve();
    }

    // Check cache unless forced
    if (!forceRefresh) {
      var cached = localStorage.getItem(STORAGE_IDS_KEY);
      var ts = parseInt(localStorage.getItem(STORAGE_IDS_TS) || '0', 10);
      if (cached && (Date.now() - ts) < IDS_CACHE_MS) {
        try {
          var parsed = JSON.parse(cached);
          _variantIds = new Set(parsed.variant_ids || []);
          _productIds = new Set(parsed.product_ids || []);
          updateAllHearts();
          return Promise.resolve();
        } catch (e) { /* fall through */ }
      }
    }

    return apiGet('/api/storefront/wishlist/ids?email=' + encodeURIComponent(email))
      .then(function (data) {
        _variantIds = new Set(data.variant_ids || []);
        _productIds = new Set(data.product_ids || []);
        // Cache
        localStorage.setItem(STORAGE_IDS_KEY, JSON.stringify({
          variant_ids: data.variant_ids || [],
          product_ids: data.product_ids || [],
        }));
        localStorage.setItem(STORAGE_IDS_TS, String(Date.now()));
        updateAllHearts();
      })
      .catch(function (err) {
        console.warn('[IM Wishlist] Failed to load IDs:', err);
      });
  }

  function addItem(productData) {
    var email = getEmail();
    if (!email) {
      showEmailCapture(function () { addItem(productData); });
      return Promise.resolve();
    }

    var payload = {
      email: email,
      shopify_variant_id: productData.variantId,
      shopify_product_id: productData.productId,
      product_title: productData.title || '',
      variant_title: productData.variantTitle || '',
      product_handle: productData.handle || '',
      image_url: productData.imageUrl || '',
      price_at_add: normalizeCurrency(productData.price),
      sku: productData.sku || '',
      compare_at_price: normalizeCurrency(productData.compareAtPrice),
      source_page: productData.sourcePage || null,
    };

    // Optimistic update
    _variantIds.add(String(productData.variantId));
    _productIds.add(String(productData.productId));
    updateAllHearts();
    updateWishlistCount();
    if (productData.title) showToast(productData.title);

    return apiPost('/api/storefront/wishlist/add', payload)
      .then(function (data) {
        if (!data.ok) {
          // Rollback both Sets so heart state stays consistent
          _variantIds.delete(String(productData.variantId));
          _productIds.delete(String(productData.productId));
          updateAllHearts();
          console.warn('[IM Wishlist] Add failed:', data.error);
        } else {
          trackWishlistEvent('wishlist_item_added', productData, {
            already_active: !!data.already_active,
            reactivated: !!data.reactivated
          });
        }
        // Refresh cache
        invalidateCache();
      })
      .catch(function (err) {
        // Rollback both Sets so heart state stays consistent
        _variantIds.delete(String(productData.variantId));
        _productIds.delete(String(productData.productId));
        updateAllHearts();
        console.error('[IM Wishlist] Add error:', err);
      });
  }

  function removeItem(variantId, productId) {
    var email = getEmail();
    if (!email) return Promise.resolve();

    // Optimistic update
    _variantIds.delete(String(variantId));
    if (productId) {
      // Only remove productId if no other wishlisted variant maps to this product
      var otherVariantExists = false;
      var sameProdBtns = document.querySelectorAll('[data-wishlist-btn][data-product-id="' + String(productId) + '"]');
      for (var bi = 0; bi < sameProdBtns.length; bi++) {
        var otherVid = sameProdBtns[bi].getAttribute('data-variant-id');
        if (otherVid && otherVid !== String(variantId) && _variantIds.has(String(otherVid))) {
          otherVariantExists = true;
          break;
        }
      }
      if (!otherVariantExists) _productIds.delete(String(productId));
    }
    updateAllHearts();
    updateWishlistCount();

    return apiPost('/api/storefront/wishlist/remove', {
      email: email,
      shopify_variant_id: variantId,
    })
      .then(function (data) {
        if (data && data.removed === 0) {
          // Server says nothing was removed — rollback optimistic update
          _variantIds.add(String(variantId));
          if (productId) _productIds.add(String(productId));
          updateAllHearts();
          console.warn('[IM Wishlist] Remove rollback: server removed 0');
        } else {
          trackWishlistEvent('wishlist_item_removed', {
            variantId: variantId,
            productId: productId
          }, {
            removed: data && typeof data.removed === 'number' ? data.removed : null
          });
        }
        invalidateCache();
      })
      .catch(function (err) {
        // Rollback
        _variantIds.add(String(variantId));
        if (productId) _productIds.add(String(productId));
        updateAllHearts();
        console.error('[IM Wishlist] Remove error:', err);
      });
  }

  function invalidateCache() {
    localStorage.removeItem(STORAGE_IDS_KEY);
    localStorage.removeItem(STORAGE_IDS_TS);
  }

  // ── Heart Button State ─────────────────────────────────────────────────

  function isWishlisted(variantId, productId) {
    // Match on variantId only — productId fallback was causing wrong heart state
    // on collection cards when a different variant of the same product was wishlisted
    return _variantIds.has(String(variantId));
  }

  function updateAllHearts() {
    var buttons = document.querySelectorAll('[data-wishlist-btn]');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var vid = btn.getAttribute('data-variant-id');
      var pid = btn.getAttribute('data-product-id');
      var active = isWishlisted(vid, pid);
      btn.classList.toggle('im-wishlist-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      // Update SVG
      var svg = btn.querySelector('svg');
      if (svg) {
        svg.setAttribute('fill', active ? 'currentColor' : 'none');
      }
    }
  }

  function updateWishlistCount() {
    var countEls = document.querySelectorAll('[data-wishlist-count]');
    var count = _variantIds.size;
    for (var i = 0; i < countEls.length; i++) {
      countEls[i].textContent = count;
      countEls[i].style.display = count > 0 ? '' : 'none';
    }
    updateFloatingBtn();
  }

  // ── Floating Button + Toast ─────────────────────────────────────────────

  function showToast(productName) {
    var existing = document.getElementById('im-wl-toast');
    if (existing) existing.remove();
    var toast = document.createElement('div');
    toast.id = 'im-wl-toast';
    toast.className = 'im-wl-toast';
    toast.innerHTML =
      '<div class="im-wl-toast-icon">' +
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' +
          '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>' +
        '</svg>' +
      '</div>' +
      '<div class="im-wl-toast-name">' +
        '<span class="im-wl-toast-label">Added to Wishlist</span>' +
        escHtml(productName) +
      '</div>';
    document.body.appendChild(toast);
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        toast.classList.add('im-wl-toast-visible');
      });
    });
    setTimeout(function () {
      toast.classList.remove('im-wl-toast-visible');
      setTimeout(function () { if (toast.parentNode) toast.remove(); }, 300);
    }, 3000);
  }

  function updateFloatingBtn() {
    var btn = document.getElementById('im-wishlist-floating');
    if (!btn) return;
    var count = _variantIds.size;
    var badge = btn.querySelector('.im-wishlist-floating-badge');
    if (count > 0) {
      btn.classList.add('im-wishlist-floating-visible');
      if (badge) {
        badge.textContent = count;
        badge.classList.add('im-wishlist-floating-badge-visible');
      }
    } else {
      btn.classList.remove('im-wishlist-floating-visible');
      if (badge) badge.classList.remove('im-wishlist-floating-badge-visible');
    }
  }

  function initFloatingBtn() {
    if (document.getElementById('im-wishlist-floating')) return;
    var btn = document.createElement('a');
    btn.id = 'im-wishlist-floating';
    btn.className = 'im-wishlist-floating';
    btn.href = '/pages/wishlist';
    btn.setAttribute('aria-label', 'View wishlist');
    btn.innerHTML =
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
        '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>' +
      '</svg>' +
      '<span class="im-wishlist-floating-badge" aria-live="polite"></span>';
    document.body.appendChild(btn);
    updateFloatingBtn();
  }

  // ── Email Capture Modal ────────────────────────────────────────────────

  function showEmailCapture(onSuccess) {
    // Don't show if already on page
    if (document.getElementById('im-wishlist-email-modal')) return;

    var previousActive = document.activeElement;
    var prevHtmlOverflow = document.documentElement.style.overflow;
    var prevBodyOverflow = document.body.style.overflow;
    var overlay = document.createElement('div');
    overlay.id = 'im-wishlist-email-modal';
    overlay.className = 'im-wl-overlay';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    overlay.innerHTML =
      '<div class="im-wl-modal" role="dialog" aria-modal="true" aria-labelledby="im-wl-modal-title" aria-describedby="im-wl-modal-desc" tabindex="-1">' +
        '<button class="im-wl-modal-close" aria-label="Close">&times;</button>' +
        '<div class="im-wl-modal-icon">' +
          '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">' +
            '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>' +
          '</svg>' +
        '</div>' +
        '<h3 class="im-wl-modal-title" id="im-wl-modal-title">Save to your wishlist</h3>' +
        '<p class="im-wl-modal-desc" id="im-wl-modal-desc">Enter your email to save items and receive price drop alerts.</p>' +
        '<form class="im-wl-modal-form">' +
          '<input type="email" class="im-wl-modal-input" placeholder="your@email.com" required autocomplete="email" />' +
          '<button type="submit" class="im-wl-modal-submit">Save</button>' +
        '</form>' +
        '<p class="im-wl-modal-privacy">Receive wishlist alerts and occasional updates from Interior Moderna. Unsubscribe anytime.</p>' +
      '</div>';

    document.body.appendChild(overlay);

    // Events
    var modal = overlay.querySelector('.im-wl-modal');
    var closeBtn = overlay.querySelector('.im-wl-modal-close');
    var form = overlay.querySelector('.im-wl-modal-form');
    var input = overlay.querySelector('.im-wl-modal-input');

    function getFocusableEls() {
      return modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    }

    function close() {
      document.removeEventListener('keydown', onKeydown);
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      overlay.classList.add('im-wl-overlay-out');
      setTimeout(function () {
        overlay.remove();
        if (previousActive && typeof previousActive.focus === 'function') previousActive.focus();
      }, 200);
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        close();
        return;
      }

      if (e.key !== 'Tab') return;

      var focusable = getFocusableEls();
      if (!focusable.length) {
        e.preventDefault();
        modal.focus();
        return;
      }

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeydown);
    closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) close();
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = input.value;
      if (setEmail(email)) {
        close();
        loadWishlistIds(true).then(function () {
          if (onSuccess) onSuccess();
        });
      }
    });

    // Focus input
    requestAnimationFrame(function () {
      modal.focus();
      input.focus();
    });
  }

  // ── Heart Button Click Handler ─────────────────────────────────────────

  function handleHeartClick(e) {
    e.preventDefault();
    e.stopPropagation();
    var btn = e.currentTarget;

    // LOCK: ignore double-clicks while an API call is already in-flight for this button.
    // Without this, a rapid double-click would fire addItem() then immediately removeItem()
    // (or vice versa) because the optimistic update changes _variantIds between the two clicks.
    if (btn.hasAttribute('data-wl-pending')) return;
    btn.setAttribute('data-wl-pending', '1');

    var vid = btn.getAttribute('data-variant-id');
    var pid = btn.getAttribute('data-product-id');

    if (!vid || !pid) {
      btn.removeAttribute('data-wl-pending');
      return;
    }

    // Pulse animation
    btn.classList.remove('im-wishlist-pulse');
    void btn.offsetWidth; // trigger reflow
    btn.classList.add('im-wishlist-pulse');

    function unlock() { btn.removeAttribute('data-wl-pending'); }

    if (isWishlisted(vid, pid)) {
      removeItem(vid, pid).then(unlock, unlock);
    } else {
      addItem({
        variantId: vid,
        productId: pid,
        title: btn.getAttribute('data-product-title') || '',
        variantTitle: btn.getAttribute('data-variant-title') || '',
        handle: btn.getAttribute('data-product-handle') || '',
        imageUrl: btn.getAttribute('data-image-url') || '',
        price: btn.getAttribute('data-price') || null,
        sku: btn.getAttribute('data-sku') || '',
        compareAtPrice: btn.getAttribute('data-compare-at-price') || null,
        context: btn.getAttribute('data-context') || null,
        sourcePage: window.location.pathname || null,
      }).then(unlock, unlock);
    }
  }

  // ── Collection Card Heart Injection ──────────────────────────────────
  // Since the theme's collection grid uses native Shopify rendering,
  // we inject heart buttons onto product cards via JS after page load.

  function syncCollectionButtonFromCard(card) {
    var btn = card && card.querySelector('[data-wishlist-btn]');
    if (!btn) return false;
    var productId = card.getAttribute('data-product-id') || '';
    var variantId = card.getAttribute('data-variant-id') || '';
    var title = card.getAttribute('data-wl-product-title') || '';
    var handle = card.getAttribute('data-wl-product-handle') || card.getAttribute('handle') || '';
    var imageUrl = card.getAttribute('data-wl-image-url') || '';
    var price = card.getAttribute('data-wl-price') || '';
    var variantTitle = card.getAttribute('data-wl-variant-title') || '';
    var sku = card.getAttribute('data-wl-sku') || '';
    var compareAtPrice = card.getAttribute('data-wl-compare-at-price') || '';

    if (productId) btn.setAttribute('data-product-id', productId);
    if (variantId) btn.setAttribute('data-variant-id', variantId);
    if (title) btn.setAttribute('data-product-title', title);
    if (handle) btn.setAttribute('data-product-handle', handle);
    if (imageUrl) btn.setAttribute('data-image-url', imageUrl);
    if (price) btn.setAttribute('data-price', price);
    if (variantTitle) btn.setAttribute('data-variant-title', variantTitle);
    if (sku) btn.setAttribute('data-sku', sku);
    if (compareAtPrice) btn.setAttribute('data-compare-at-price', compareAtPrice);
    return true;
  }

  function hydrateCollectionCardFromProductJson(card, handle) {
    if (!card || !handle || card.hasAttribute('data-wl-fetching') || card.hasAttribute('data-wl-fetch-failed')) return;
    card.setAttribute('data-wl-fetching', '1');

    fetch('/products/' + encodeURIComponent(handle) + '.json')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (json) {
        var product = json && json.product;
        if (!product) return;
        var variants = product.variants || [];
        var currentVariantId = card.getAttribute('data-variant-id') || '';
        var variant = null;
        for (var i = 0; i < variants.length; i++) {
          if (currentVariantId && String(variants[i].id) === String(currentVariantId)) {
            variant = variants[i];
            break;
          }
        }
        for (var ai = 0; !variant && ai < variants.length; ai++) {
          if (variants[ai].available) {
            variant = variants[ai];
            break;
          }
        }
        if (!variant && variants.length) variant = variants[0];

        if (product.id) card.setAttribute('data-product-id', String(product.id));
        if (product.title) card.setAttribute('data-wl-product-title', product.title);
        if (product.handle) card.setAttribute('data-wl-product-handle', product.handle);
        if (product.image && product.image.src) {
          card.setAttribute('data-wl-image-url', product.image.src);
        } else if (product.images && product.images[0] && product.images[0].src) {
          card.setAttribute('data-wl-image-url', product.images[0].src);
        }

        if (variant) {
          if (variant.id) card.setAttribute('data-variant-id', String(variant.id));
          if (variant.title) card.setAttribute('data-wl-variant-title', variant.title);
          if (variant.sku) card.setAttribute('data-wl-sku', variant.sku);
          if (variant.price) card.setAttribute('data-wl-price', String(variant.price));
          if (variant.compare_at_price) card.setAttribute('data-wl-compare-at-price', String(variant.compare_at_price));
        }
      })
      .catch(function () {
        card.setAttribute('data-wl-fetch-failed', '1');
      })
      .then(function () {
        card.removeAttribute('data-wl-fetching');
        syncCollectionButtonFromCard(card);
        if (card.getAttribute('data-product-id') && card.getAttribute('data-variant-id')) {
          injectCollectionHearts();
          updateAllHearts();
        } else {
          card.setAttribute('data-wl-fetch-failed', '1');
        }
      });
  }

  function injectCollectionHearts() {
    // Target product cards in collection grids — common selectors for Impact theme
    var cards = document.querySelectorAll('product-card, .product-card, .card--product');
    if (!cards.length) return;

    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];

      // Find the product link to extract handle and product data
      var link = card.querySelector('a[href*="/products/"]');
      var handle = card.getAttribute('handle') || '';
      if (!handle && link) {
        var href = link.getAttribute('href') || '';
        var handleMatch = href.match(/\/products\/([^?#/]+)/);
        if (handleMatch) handle = handleMatch[1];
      }
      if (!handle) continue;

      // If a previous script pass already injected the button, enrich missing
      // product data rather than leaving analytics payloads half-empty.
      var existingBtn = card.querySelector('[data-wishlist-btn]');
      if (existingBtn) {
        if (
          (!existingBtn.getAttribute('data-product-id') ||
            !existingBtn.getAttribute('data-variant-id') ||
            !existingBtn.getAttribute('data-price')) &&
          !card.hasAttribute('data-wl-fetch-failed')
        ) {
          hydrateCollectionCardFromProductJson(card, handle);
        } else {
          syncCollectionButtonFromCard(card);
        }
        continue;
      }

      // Find the image container to overlay the heart
      var imgContainer = card.querySelector('.product-card__figure, .product-card__image, .card__media, .card-media, .card__inner');
      if (!imgContainer) {
        // Fallback: use first link that contains an img
        var imgLink = card.querySelector('a img');
        if (imgLink) imgContainer = imgLink.parentElement;
      }
      if (!imgContainer) continue;

      // Get product data from the card's JSON or data attributes
      var productId = card.getAttribute('data-product-id') || '';
      var variantId = card.getAttribute('data-variant-id') || '';
      var productTitle = card.getAttribute('data-wl-product-title') || '';
      var variantTitle = card.getAttribute('data-wl-variant-title') || '';
      var imageUrl = card.getAttribute('data-wl-image-url') || '';
      var price = card.getAttribute('data-wl-price') || '';
      var sku = card.getAttribute('data-wl-sku') || '';
      var compareAtPrice = card.getAttribute('data-wl-compare-at-price') || '';

      // Try to get product ID from the card's product-card element
      if (!productId) {
        var productIdEl = card.querySelector('[data-product-id]');
        if (productIdEl) productId = productIdEl.getAttribute('data-product-id');
      }

      // Try data-wpd-* attributes (Impact theme / other apps)
      if (!productId) {
        var wpdEl = card.querySelector('[data-wpd-product-id]');
        if (wpdEl) productId = wpdEl.getAttribute('data-wpd-product-id');
      }
      if (!variantId) {
        var wpdVEl = card.querySelector('[data-wpd-variant-id]');
        if (wpdVEl) variantId = wpdVEl.getAttribute('data-wpd-variant-id');
      }

      // Try to get from a JSON script tag within the card
      var jsonScript = card.querySelector('script[type="application/json"]');
      if (jsonScript) {
        try {
          var cardData = JSON.parse(jsonScript.textContent);
          if (cardData.id) productId = String(cardData.id);
          if (cardData.variants && cardData.variants[0]) {
            variantId = String(cardData.variants[0].id);
            if (cardData.variants[0].title) variantTitle = cardData.variants[0].title;
            if (cardData.variants[0].sku) sku = cardData.variants[0].sku;
            if (cardData.variants[0].compare_at_price) compareAtPrice = String(cardData.variants[0].compare_at_price);
          }
          if (cardData.title) productTitle = cardData.title;
          if (cardData.featured_image) imageUrl = cardData.featured_image;
          if (cardData.price) price = (cardData.price / 100).toFixed(2);
        } catch (e) { /* ignore */ }
      }

      // Get title from card text if not from JSON
      if (!productTitle) {
        var titleEl = card.querySelector('.product-card__title, .card__heading, .card-information__text a');
        if (titleEl) productTitle = titleEl.textContent.trim();
      }

      // Get image URL from img tag
      if (!imageUrl) {
        var img = card.querySelector('img');
        if (img) imageUrl = img.getAttribute('src') || '';
      }

      // Skip if we couldn't get enough data
      if ((!productId || !variantId || !price) && !card.hasAttribute('data-wl-fetch-failed')) {
        hydrateCollectionCardFromProductJson(card, handle);
        continue;
      }
      if (!productId || !variantId) continue;

      // Make the image container position:relative for overlay and add hover class
      var pos = window.getComputedStyle(imgContainer).position;
      if (pos === 'static') imgContainer.style.position = 'relative';
      imgContainer.classList.add('im-wishlist-card-wrap');

      // Create heart button
      var heartBtn = document.createElement('button');
      heartBtn.type = 'button';
      heartBtn.className = 'im-wishlist-card-btn';
      heartBtn.setAttribute('data-wishlist-btn', '');
      heartBtn.setAttribute('data-context', 'collection-card');
      heartBtn.setAttribute('data-variant-id', variantId);
      heartBtn.setAttribute('data-product-id', productId);
      heartBtn.setAttribute('data-product-title', productTitle);
      heartBtn.setAttribute('data-product-handle', handle);
      heartBtn.setAttribute('data-image-url', imageUrl);
      heartBtn.setAttribute('data-price', price);
      heartBtn.setAttribute('data-variant-title', variantTitle);
      heartBtn.setAttribute('data-sku', sku);
      heartBtn.setAttribute('data-compare-at-price', compareAtPrice);
      heartBtn.setAttribute('aria-label', 'Add to wishlist');
      heartBtn.setAttribute('aria-pressed', 'false');
      heartBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>';

      heartBtn.addEventListener('click', handleHeartClick);
      heartBtn.setAttribute('data-wl-bound', '1');
      imgContainer.appendChild(heartBtn);
    }

    // Update heart states after injection
    updateAllHearts();
  }

  // ── Wishlist Page — Enrichment + Sort/Filter ────────────────────────────

  /**
   * Fetch live Shopify data for all wishlist items.
   * Builds _wlEnrichMap: variantId -> { currentPrice, available, compareAtPrice, inventoryQty }
   */
  function _enrichWishlistItems(items, cb) {
    var handleMap = {};
    for (var i = 0; i < items.length; i++) {
      var h = items[i].product_handle;
      if (h && /^[a-z0-9][a-z0-9-]*$/.test(h)) {
        if (!handleMap[h]) handleMap[h] = [];
        handleMap[h].push(items[i]);
      }
    }
    var handles = Object.keys(handleMap);
    var enrichMap = {};
    var done = 0;
    if (handles.length === 0) { cb(enrichMap); return; }

    for (var k = 0; k < handles.length; k++) {
      (function (handle) {
        fetch('/products/' + handle + '.json')
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (json) {
            if (json && json.product && json.product.variants) {
              var variants = json.product.variants;
              for (var v = 0; v < variants.length; v++) {
                var vr = variants[v];
                enrichMap[String(vr.id)] = {
                  currentPrice: parseFloat(vr.price) || 0,
                  available: !!vr.available,
                  compareAtPrice: vr.compare_at_price ? parseFloat(vr.compare_at_price) : null,
                  inventoryQty: typeof vr.inventory_quantity === 'number' ? vr.inventory_quantity : null
                };
              }
            }
          })
          .catch(function () { /* silently skip failed fetches */ })
          .then(function () {
            done++;
            if (done >= handles.length) cb(enrichMap);
          });
      })(handles[k]);
    }
  }

  /**
   * Apply current sort + filter state to _wlRawItems, return filtered/sorted array.
   */
  function _applyWishlistSortFilter() {
    if (!_wlRawItems) return [];
    var items = _wlRawItems.slice();
    var map = _wlEnrichMap || {};

    // Filters
    if (_wlFilters.priceDrop) {
      items = items.filter(function (it) {
        var e = map[String(it.shopify_variant_id)];
        return e && it.price_at_add && e.currentPrice < parseFloat(it.price_at_add);
      });
    }
    if (_wlFilters.onSale) {
      items = items.filter(function (it) {
        var e = map[String(it.shopify_variant_id)];
        return e && e.compareAtPrice && e.currentPrice < e.compareAtPrice;
      });
    }
    if (_wlFilters.inStock) {
      items = items.filter(function (it) {
        var e = map[String(it.shopify_variant_id)];
        return e && e.available;
      });
    }

    // Sort
    items.sort(function (a, b) {
      var eA = map[String(a.shopify_variant_id)] || {};
      var eB = map[String(b.shopify_variant_id)] || {};
      switch (_wlSort) {
        case 'date-asc':
          return (a.created_at || '') < (b.created_at || '') ? -1 : 1;
        case 'price-asc':
          return (eA.currentPrice || 0) - (eB.currentPrice || 0);
        case 'price-desc':
          return (eB.currentPrice || 0) - (eA.currentPrice || 0);
        case 'name-az':
          return (a.product_title || '').localeCompare(b.product_title || '');
        default: // date-desc
          return (b.created_at || '') > (a.created_at || '') ? 1 : -1;
      }
    });

    return items;
  }

  /**
   * Render toolbar (sort + filters) + card grid using enriched data.
   */
  function _renderWishlistGrid() {
    var container = _wlPageContainer;
    if (!container) return;
    var items = _applyWishlistSortFilter();
    var map = _wlEnrichMap || {};
    var total = (_wlRawItems || []).length;

    // ── Toolbar ──
    var toolbar =
      '<div class="im-wl-toolbar">' +
        '<div class="im-wl-filters">' +
          '<button class="im-wl-filter-pill' + (_wlFilters.priceDrop ? ' active' : '') + '" data-filter="priceDrop">Price Drop</button>' +
          '<button class="im-wl-filter-pill' + (_wlFilters.onSale ? ' active' : '') + '" data-filter="onSale">On Sale</button>' +
          '<button class="im-wl-filter-pill' + (_wlFilters.inStock ? ' active' : '') + '" data-filter="inStock">In Stock</button>' +
        '</div>' +
        '<select class="im-wl-sort-select" aria-label="Sort wishlist">' +
          '<option value="date-desc"' + (_wlSort === 'date-desc' ? ' selected' : '') + '>Newest First</option>' +
          '<option value="date-asc"' + (_wlSort === 'date-asc' ? ' selected' : '') + '>Oldest First</option>' +
          '<option value="price-asc"' + (_wlSort === 'price-asc' ? ' selected' : '') + '>Price: Low \u2192 High</option>' +
          '<option value="price-desc"' + (_wlSort === 'price-desc' ? ' selected' : '') + '>Price: High \u2192 Low</option>' +
          '<option value="name-az"' + (_wlSort === 'name-az' ? ' selected' : '') + '>Name: A \u2192 Z</option>' +
        '</select>' +
      '</div>';

    // ── Header ──
    var html = '<div class="im-wl-page-header"><h2>Your Wishlist</h2><span class="im-wl-page-count">' +
      total + ' item' + (total !== 1 ? 's' : '') +
      (items.length < total ? ' (' + items.length + ' shown)' : '') +
      '</span></div>';
    html += toolbar;

    if (items.length === 0 && total > 0) {
      html += '<div class="im-wl-page-empty"><p>No items match your filters.</p></div>';
      container.innerHTML = html;
      _bindToolbarEvents(container);
      return;
    }

    // ── Cards ──
    html += '<div class="im-wl-page-grid">';
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var rawHandle = /^[a-z0-9][a-z0-9-]*$/.test(item.product_handle || '') ? item.product_handle : '';
      var link = rawHandle ? '/products/' + rawHandle : '#';
      var img = item.image_url || '';
      var vid = parseInt(item.shopify_variant_id, 10) || 0;
      var pid = parseInt(item.shopify_product_id, 10) || 0;
      var titleEsc = escHtml(item.product_title || 'Product');
      var variantEsc = escHtml(item.variant_title || '');
      var imgEsc = escHtml(img);
      var enrich = map[String(vid)] || {};
      var savedPrice = item.price_at_add ? parseFloat(item.price_at_add) : null;
      var curPrice = enrich.currentPrice || savedPrice || 0;
      var isAvailable = enrich.available !== false;
      var invQty = enrich.inventoryQty;
      var isPriceDrop = savedPrice && curPrice < savedPrice;
      var isOnSale = enrich.compareAtPrice && curPrice < enrich.compareAtPrice;
      var isLowStock = isAvailable && typeof invQty === 'number' && invQty > 0 && invQty < 5;

      // ── Badges ──
      var badges = '';
      if (isPriceDrop) badges += '<span class="im-wl-badge im-wl-badge-drop">Price Drop</span>';
      if (isOnSale) badges += '<span class="im-wl-badge im-wl-badge-sale">Sale</span>';
      if (isLowStock) badges += '<span class="im-wl-badge im-wl-badge-low">Only ' + invQty + ' left</span>';

      // ── Price display ──
      var priceHtml = '';
      if (isPriceDrop) {
        priceHtml = '<span class="im-wl-price-wrap">' +
          '<span class="im-wl-price-old">$' + savedPrice.toFixed(2) + '</span>' +
          '<span class="im-wl-price-drop">$' + curPrice.toFixed(2) + '</span>' +
        '</span>';
      } else if (isOnSale) {
        priceHtml = '<span class="im-wl-price-wrap">' +
          '<span class="im-wl-price-old">$' + enrich.compareAtPrice.toFixed(2) + '</span>' +
          '<span class="im-wl-price-sale">$' + curPrice.toFixed(2) + '</span>' +
        '</span>';
      } else if (curPrice) {
        priceHtml = '<span class="im-wl-page-card-price">$' + curPrice.toFixed(2) + '</span>';
      }

      // ── Action button ──
      var actionBtn = '';
      if (!isAvailable) {
        actionBtn =
          '<span class="im-wl-sold-out-label">Sold Out</span>' +
          '<button class="im-wl-page-card-notify" data-vid="' + vid + '" data-pid="' + pid + '" data-handle="' + escHtml(rawHandle) + '">Notify When Back</button>';
      } else if (vid) {
        actionBtn = '<button class="im-wl-page-card-move" data-variant-id="' + vid + '" data-vid="' + vid + '" data-pid="' + pid + '" aria-label="Move to cart">Move to Cart</button>';
      }

      html +=
        '<div class="im-wl-page-card" data-variant-id="' + vid + '">' +
          (badges ? '<div class="im-wl-page-card-badges">' + badges + '</div>' : '') +
          '<a href="' + link + '" class="im-wl-page-card-img">' +
            (img ? '<img src="' + imgEsc + '" alt="' + titleEsc + '" loading="lazy" />' : '<div class="im-wl-page-card-placeholder"></div>') +
          '</a>' +
          '<div class="im-wl-page-card-info">' +
            '<a href="' + link + '" class="im-wl-page-card-title">' + titleEsc + '</a>' +
            (variantEsc && item.variant_title !== 'Default Title' ? '<span class="im-wl-page-card-variant">' + variantEsc + '</span>' : '') +
            priceHtml +
            actionBtn +
          '</div>' +
          '<button class="im-wl-page-card-remove" data-vid="' + vid + '" data-pid="' + pid + '" aria-label="Remove from wishlist">' +
            '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
          '</button>' +
        '</div>';
    }
    html += '</div>';
    container.innerHTML = html;
    _bindToolbarEvents(container);
    _bindCardEvents(container);
  }

  /**
   * Bind sort dropdown + filter pill events.
   */
  function _bindToolbarEvents(container) {
    // Filter pills
    var pills = container.querySelectorAll('.im-wl-filter-pill');
    for (var i = 0; i < pills.length; i++) {
      pills[i].addEventListener('click', function (e) {
        var key = e.currentTarget.getAttribute('data-filter');
        if (key && _wlFilters.hasOwnProperty(key)) {
          _wlFilters[key] = !_wlFilters[key];
          _renderWishlistGrid();
        }
      });
    }
    // Sort select
    var sortSel = container.querySelector('.im-wl-sort-select');
    if (sortSel) {
      sortSel.addEventListener('change', function (e) {
        _wlSort = e.target.value;
        _renderWishlistGrid();
      });
    }
  }

  /**
   * Bind remove, move-to-cart, and notify-when-back buttons.
   */
  function _bindCardEvents(container) {
    // ── Remove buttons ──
    var removeBtns = container.querySelectorAll('.im-wl-page-card-remove');
    for (var j = 0; j < removeBtns.length; j++) {
      removeBtns[j].addEventListener('click', function (e) {
        var btn = e.currentTarget;
        var vid = btn.getAttribute('data-vid');
        var pid = btn.getAttribute('data-pid');
        var card = btn.closest('.im-wl-page-card');
        if (card) { card.style.opacity = '0.3'; card.style.pointerEvents = 'none'; }
        removeItem(vid, pid).then(function () {
          // Remove from raw items and re-render
          if (_wlRawItems) {
            _wlRawItems = _wlRawItems.filter(function (it) { return String(it.shopify_variant_id) !== String(vid); });
          }
          _renderWishlistGrid();
        });
      });
    }

    // ── Move to Cart (add to cart + remove from wishlist) ──
    var moveBtns = container.querySelectorAll('.im-wl-page-card-move');
    for (var m = 0; m < moveBtns.length; m++) {
      moveBtns[m].addEventListener('click', function (e) {
        var btn = e.currentTarget;
        var vid = btn.getAttribute('data-variant-id');
        var pidAttr = btn.getAttribute('data-pid');
        if (!vid) return;
        btn.disabled = true;
        btn.textContent = 'Adding\u2026';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(vid, 10), quantity: 1 }] })
        })
        .then(function (r) {
          if (!r.ok) {
            return r.json().then(function (err) { throw new Error(err.description || 'Sold out'); });
          }
          return r.json();
        })
        .then(function (data) {
          if (data && data.items && data.items.length) {
            btn.textContent = 'Added \u2714';
            btn.classList.add('im-wl-card-atc-added');
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            var movedItem = (_wlRawItems || []).find(function (it) {
              return String(it.shopify_variant_id) === String(vid);
            }) || {};
            trackWishlistEvent('wishlist_item_moved_to_cart', {
              variantId: vid,
              productId: pidAttr,
              handle: movedItem.product_handle,
              sku: movedItem.sku,
              price: movedItem.price_at_add,
              sourcePage: window.location.pathname,
              context: 'wishlist-page'
            });
            // Remove from wishlist after successful ATC
            removeItem(vid, pidAttr).then(function () {
              if (_wlRawItems) {
                _wlRawItems = _wlRawItems.filter(function (it) { return String(it.shopify_variant_id) !== String(vid); });
              }
              setTimeout(function () { _renderWishlistGrid(); }, 1200);
            });
          } else {
            btn.textContent = 'Move to Cart';
            btn.disabled = false;
          }
        })
        .catch(function (err) {
          btn.textContent = (err && err.message) || 'Unavailable';
          btn.classList.add('im-wl-card-atc-error');
          setTimeout(function () {
            btn.textContent = 'Move to Cart';
            btn.disabled = false;
            btn.classList.remove('im-wl-card-atc-error');
          }, 3000);
        });
      });
    }

    // ── Notify When Back buttons ──
    var notifyBtns = container.querySelectorAll('.im-wl-page-card-notify');
    for (var n = 0; n < notifyBtns.length; n++) {
      notifyBtns[n].addEventListener('click', function (e) {
        var btn = e.currentTarget;
        if (btn.classList.contains('im-wl-notify-done')) return;
        var vid = btn.getAttribute('data-vid');
        var pid = btn.getAttribute('data-pid');
        var handle = btn.getAttribute('data-handle');
        btn.disabled = true;
        btn.textContent = 'Subscribing\u2026';
        apiPost('/api/storefront/wishlist/notify-restock', {
          email: getEmail(),
          shopify_variant_id: vid,
          shopify_product_id: pid,
          product_handle: handle
        }).then(function () {
          btn.textContent = 'Subscribed \u2714';
          btn.classList.add('im-wl-notify-done');
          trackWishlistEvent('wishlist_restock_requested', {
            variantId: vid,
            productId: pid,
            handle: handle,
            sourcePage: window.location.pathname,
            context: 'wishlist-page'
          });
        }).catch(function () {
          btn.textContent = 'Notify When Back';
          btn.disabled = false;
        });
      });
    }
  }

  // ── Wishlist Page ──────────────────────────────────────────────────────

  function renderWishlistPage() {
    var container = document.getElementById('im-wishlist-page');
    if (!container) return;

    var email = getEmail();
    if (!email) {
      container.innerHTML =
        '<div class="im-wl-page-empty">' +
          '<svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" opacity="0.35">' +
            '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>' +
          '</svg>' +
          '<h2>Your Wishlist</h2>' +
          '<p>Enter your email to view saved items.</p>' +
          '<button class="im-wl-page-email-btn" onclick="window.IMWishlist.showEmailCapture(function(){ window.IMWishlist.renderWishlistPage(); })">Enter Email</button>' +
        '</div>';
      return;
    }

    _wlPageContainer = container;
    container.innerHTML = '<div class="im-wl-page-loading">Loading your wishlist\u2026</div>';

    apiGet('/api/storefront/wishlist/enriched?email=' + encodeURIComponent(email))
      .then(function (data) {
        var items = data.items || [];
        if (items.length === 0) {
          container.innerHTML =
            '<div class="im-wl-page-empty">' +
              '<svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" opacity="0.35">' +
                '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>' +
              '</svg>' +
              '<h2>Your Wishlist</h2>' +
              '<p>You haven\'t saved any items yet. Browse our catalog and tap the heart icon to save pieces you love.</p>' +
              '<a href="/collections/all" class="im-wl-page-browse-btn">Browse the Catalog</a>' +
            '</div>';
          return;
        }

        _wlRawItems = items;
        _wlEnrichMap = data.enrich_map || {};

        if (Object.keys(_wlEnrichMap).length) {
          _renderWishlistGrid();
          return;
        }

        container.innerHTML = '<div class="im-wl-page-loading">Loading prices\u2026</div>';
        _enrichWishlistItems(items, function (enrichMap) {
          _wlEnrichMap = enrichMap;
          _renderWishlistGrid();
        });
      })
      .catch(function (err) {
        container.innerHTML = '<div class="im-wl-page-empty"><p>Unable to load wishlist. Please try again.</p></div>';
        console.error('[IM Wishlist] Page load error:', err);
      });
  }

  // ── Initialization ─────────────────────────────────────────────────────

  function init() {
    if (_initialized) return;
    _initialized = true;

    // Inject hearts on collection cards (JS-based, since theme grid is native Shopify)
    injectCollectionHearts();

    // Render floating wishlist corner button (appears when items are saved)
    initFloatingBtn();

    // Bind all heart buttons (including those from Liquid snippets)
    var buttons = document.querySelectorAll('[data-wishlist-btn]');
    for (var i = 0; i < buttons.length; i++) {
      // Only bind if not already bound (collection hearts get bound during injection)
      if (!buttons[i].hasAttribute('data-wl-bound')) {
        buttons[i].addEventListener('click', handleHeartClick);
        buttons[i].setAttribute('data-wl-bound', '1');
      }
    }

    // ── Keyboard support for product-page heart (div role="button") ────
    // The product-page heart uses <div role="button"> instead of <button> to
    // avoid UPcart's stopImmediatePropagation on <button> clicks. We add
    // Enter/Space key handling here for accessibility.
    var productHeart = document.querySelector('[data-wishlist-btn][data-context="product-page"]');
    if (productHeart && productHeart.tagName !== 'BUTTON') {
      productHeart.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          productHeart.click();
        }
      });
    }

    // Load IDs if we have an email
    loadWishlistIds(false).then(function () {
      updateWishlistCount();
    });

    // Render wishlist page if present
    renderWishlistPage();

    // Listen for variant changes on product pages (update heart data attributes)
    document.addEventListener('variant:change', function (e) {
      var variant = e.detail && e.detail.variant;
      if (!variant) return;
      // Update both the product-page heart (ATC row) and the gallery overlay heart
      var productBtns = document.querySelectorAll(
        '[data-wishlist-btn][data-context="product-page"], .im-wishlist-gallery-btn-wrap [data-wishlist-btn]'
      );
      for (var vi = 0; vi < productBtns.length; vi++) {
        var pb = productBtns[vi];
        pb.setAttribute('data-variant-id', variant.id);
        if (variant.price) pb.setAttribute('data-price', (variant.price / 100).toFixed(2));
        if (variant.sku) pb.setAttribute('data-sku', variant.sku);
        pb.setAttribute('data-variant-title', variant.title || '');
        if (variant.featured_image && variant.featured_image.src) {
          pb.setAttribute('data-image-url', variant.featured_image.src);
        }
        if (variant.compare_at_price) {
          pb.setAttribute('data-compare-at-price', (variant.compare_at_price / 100).toFixed(2));
        }
      }
      updateAllHearts();
    });

    // Re-inject collection hearts on Shopify section rendering (for infinite scroll, AJAX nav)
    document.addEventListener('shopify:section:load', function () {
      injectCollectionHearts();
    });
  }

  // ── Public API ─────────────────────────────────────────────────────────
  window.IMWishlist = {
    init: init,
    addItem: addItem,
    removeItem: removeItem,
    getEmail: getEmail,
    setEmail: setEmail,
    showEmailCapture: showEmailCapture,
    renderWishlistPage: renderWishlistPage,
    isWishlisted: isWishlisted,
    loadWishlistIds: loadWishlistIds,
    showToast: showToast,
    getCount: function () { return _variantIds.size; },
  };

  // Auto-init on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
